import { useEffect, useRef, useState } from "react";
import { locations, primaryLocation } from "@/lib/locations-data";
import { business } from "@/lib/business-data";

const BRAND_BLUE = "#1B3D93";
const BRAND_RED = "#D72329";

interface Props {
  /** Tailwind height classes for the map canvas. */
  heightClass?: string;
  /** Fade the bottom edge into the page background (used behind an overlay panel). */
  fadeBottom?: boolean;
}

const LocationMap = ({
  heightClass = "h-[460px] md:h-[560px] lg:h-[640px]",
  fadeBottom = true,
}: Props) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<import("leaflet").Map | null>(null);
  const [inView, setInView] = useState(false);

  /**
   * The map lives far below the fold everywhere it is used, so neither the
   * Leaflet bundle nor its tiles are fetched until the section is close to
   * being scrolled into view.
   */
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;

    const initMap = async () => {
      // Leaflet's stylesheet is loaded with the library rather than statically,
      // so it never becomes part of the render-blocking main stylesheet.
      const [L] = await Promise.all([import("leaflet"), import("leaflet/dist/leaflet.css")]);
      if (cancelled || !mapRef.current) return;

      mapInstanceRef.current?.remove();

      const map = L.map(mapRef.current, {
        zoomControl: false,
        scrollWheelZoom: false,
        dragging: true,
      }).setView([primaryLocation.lat, primaryLocation.lng], 9);

      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map);

      // Service radius
      L.circle([primaryLocation.lat, primaryLocation.lng], {
        radius: business.serviceRadiusMeters,
        color: BRAND_BLUE,
        fillColor: BRAND_BLUE,
        fillOpacity: 0.07,
        weight: 2,
        dashArray: "8 5",
      }).addTo(map);

      const primaryIcon = L.divIcon({
        className: "custom-marker-primary",
        html: `<div style="background:${BRAND_BLUE};width:34px;height:34px;border-radius:50%;border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
        </div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });

      /**
       * The 18px dot is smaller than the 24px minimum touch target, so it is
       * centred inside a transparent 26px box. The visible pin is unchanged;
       * only the tappable area grows.
       */
      const cityIcon = L.divIcon({
        className: "custom-marker-city",
        html: `<div style="width:26px;height:26px;display:flex;align-items:center;justify-content:center;">
          <span style="background:${BRAND_RED};width:18px;height:18px;border-radius:50%;border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:block;"></span>
        </div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });

      const detailButton = (href: string) => `
        <a href="${href}" style="
          display:inline-flex;align-items:center;gap:6px;margin-top:10px;
          padding:7px 13px;border-radius:8px;
          background:${BRAND_BLUE};color:#fff;
          font-size:12px;font-weight:700;text-decoration:none;
          box-shadow:0 4px 12px -2px rgba(27,61,147,0.4);
        ">
          View Details
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
        </a>`;

      locations.forEach((loc) => {
        const subtitle = loc.isPrimary
          ? "Our home base"
          : `${loc.distanceMiles} miles from Hayward`;
        const marker = L.marker([loc.lat, loc.lng], {
          icon: loc.isPrimary ? primaryIcon : cityIcon,
          // Leaflet marks every keyboard-focusable marker as role="button";
          // without this it has no accessible name for screen readers.
          title: `${loc.name}, ${loc.state} — ${subtitle}`,
          alt: `${loc.name}, ${loc.state} — ${subtitle}`,
        })
          .addTo(map)
          .bindPopup(
            `<div style="padding:6px;font-family:system-ui,sans-serif;min-width:170px;">
              <strong style="font-size:14px;color:#101B33;">${loc.name}, ${loc.state}</strong>
              <p style="color:#5a6478;font-size:12px;margin:4px 0 0;">${subtitle}</p>
              ${detailButton(`/locations/${loc.slug}`)}
            </div>`
          );
        marker.getElement()?.setAttribute("aria-label", `${loc.name}, ${loc.state} — ${subtitle}`);
      });
    };

    initMap();

    return () => {
      cancelled = true;
      mapInstanceRef.current?.remove();
      mapInstanceRef.current = null;
    };
  }, [inView]);

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${heightClass} overflow-hidden isolate`}
      style={{ zIndex: 0 }}
    >
      <div
        ref={mapRef}
        role="region"
        aria-label={`Map of the ${business.serviceRadiusMiles}-mile service area around Hayward, California`}
        className="w-full h-full"
        style={{ zIndex: 0 }}
      />
      {fadeBottom && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-64 z-[400]"
          style={{
            background:
              "linear-gradient(to bottom, transparent 0%, hsl(var(--background) / 0.85) 60%, hsl(var(--background)) 100%)",
          }}
        />
      )}
    </div>
  );
};

export default LocationMap;
